import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../contexts/PermissionsContext'
import PrinterLoader from './PrinterLoader'

// Loading component - overlay flutuante que mantém a tela de fundo visível
function LoadingSpinner() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: 'none'
      }}
    >
      <PrinterLoader size={120} label="Carregando..." />
    </div>
  )
}

// Renderiza a página (children) mantendo-a visível sob o overlay de loading.
// A forma da árvore precisa ser ESTÁVEL entre os estados (com/sem loading):
// se ela mudar, o React desmonta e remonta os children (ex.: a aba ativa do
// Tabs da página de Login é perdida durante o LOGIN_START).
const withLoadingOverlay = (children, show = true) => (
  <>
    {children}
    {show && <LoadingSpinner />}
  </>
)

// Protected Route component
function ProtectedRoute({ children, requireAuth = true, redirectTo = '/login' }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication (forma estável da árvore:
  // não desmonta os children enquanto a sessão é restaurada)
  if (isLoading) {
    return withLoadingOverlay(children)
  }

  // If route requires authentication and user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Save the attempted location for redirecting after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // If user is authenticated, render the protected component (sem overlay)
  return withLoadingOverlay(children, false)
}



// Public Route component (for login, register, etc.)
export function PublicRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication (mesma forma da árvore
  // do return normal — evita remount do /login e perda da aba ativa)
  if (isLoading) {
    return withLoadingOverlay(children)
  }

  // If user is authenticated, redirect to menu
  if (isAuthenticated) {
    // Login por romaneio: SEMPRE entra pelo módulo Onboard. A prioridade é do
    // romaneio e não de location.state.from, que pode guardar um bounce antigo
    // (ex.: página /menu sem sessão -> /login com state.from=/menu).
    const from = user?.romaneio
      ? '/onboard'
      : location.state?.from?.pathname || '/menu'
    return <Navigate to={from} replace />
  }

  // Render the public component (sem overlay)
  return withLoadingOverlay(children, false)
}

// Organization Route component (requires organization selection)
export function OrganizationRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return withLoadingOverlay(children)
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check if user has organizations or needs to create one
  // This could be enhanced to check organization context
  return withLoadingOverlay(children, false)
}






// User Route component (restricts regular users to transfers only)
export function UserRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return withLoadingOverlay(children)
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return withLoadingOverlay(children, false)
}

// Role Protected Route component (requires specific roles)
export function RoleProtectedRoute({
  children,
  requiredRoles = [],
  requireAll = false,
  redirectTo = '/menu'
}) {
  const { isAuthenticated, isLoading } = useAuth()
  const { hasRole, hasAllRoles, hasAnyRole, loading: permissionsLoading } = usePermissions()
  const location = useLocation()

  // Show loading spinner while checking authentication or permissions
  if (isLoading || permissionsLoading) {
    return withLoadingOverlay(children)
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check role requirements
  if (requiredRoles.length > 0) {
    const hasAccess = requireAll
      ? hasAllRoles(requiredRoles)
      : hasAnyRole(requiredRoles)

    if (!hasAccess) {
      // Sem a role necessária, redireciona para o destino padrão (ex.: /menu)
      // em vez de deixar o usuário preso numa tela de "Acesso Negado".
      return <Navigate to={redirectTo} replace />
    }
  }

  return withLoadingOverlay(children, false)
}


export default ProtectedRoute